import 'twin.macro'

import { Ring } from '@uiball/loaders'
import CloseIcon from 'assets/icons/close-icon.svg?component'
import CheckIcon from 'assets/icons/step-done-check.svg?component'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppDispatch, useAppSelector } from 'hooks'
import { Line as LinearProgress } from 'rc-progress'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useGetApplicationStatusQuery } from 'redux/api'
import { useLazyGetUserQuery } from 'redux/api/leadsApi'
import { getCurrentFlowType, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getCurrentFlowId, setIsRejected } from 'redux/store/slices/userSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { type GetWsStepStatus, type GetWsStepStatuses, FLOW_STEPS } from 'share/types'
import tw from 'twin.macro'
import { getRandomNumberBetweenRange, getWsStepStatus, getWsStepStatuses } from 'utils'
import { pathnames } from 'utils/constants'
import Emitter from 'utils/emitter'
import { v4 as uuidv4 } from 'uuid'

const PRESCORING_PAGE_STEPS = [
  FLOW_STEPS.PRE_SCORE,
  FLOW_STEPS.FETCH_PERSONAL_INFO,
  FLOW_STEPS.COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED,
  FLOW_STEPS.CREATE_APPLICATION_IN_TELEMARKETING,
  FLOW_STEPS.GENERATE_DOCUMENTS_SIGNED_BY_OTP
]

type StepType = {
  status: GetWsStepStatus | GetWsStepStatuses
  id: string
  title: string
}

const PROGRESS_LIMIT = getRandomNumberBetweenRange(85, 96)

const checkStepAndProcess = (status: GetWsStepStatus | GetWsStepStatuses, condition: boolean) => {
  return status === 'DONE' && condition
}

const PrescoringLoader = () => {
  // Tools
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // State
  const [progress, setProgress] = useState(0)

  // Selectors
  const {
    PRE_SCORE,
    OPERATOR_BMG,
    FETCH_PERSONAL_INFO,
    GENERATE_DOCUMENTS_SIGNED_BY_OTP,
    CREATE_APPLICATION_IN_TELEMARKETING,
    COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED
  } = useAppSelector(getNotificationsSelector)
  const currentFlowType = useAppSelector(getCurrentFlowType)
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { credit_status } = useAppSelector(getStatusSelector)

  // Queries
  const { data } = useGetApplicationStatusQuery(currentFlowId as string, {
    pollingInterval: 45000,
    skip: !currentFlowId
  })

  // Mutations
  const [fetchUser] = useLazyGetUserQuery()

  // Computed values
  const firstStepsStatus = useMemo<StepType>(() => {
    if (!currentFlowType) {
      return {
        status: 'STEP_NOT_STARTED',
        id: uuidv4(),
        title: 'flow-inner-check'
      }
    }
    return {
      status: getWsStepStatuses(
        FETCH_PERSONAL_INFO,
        COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED,
        CREATE_APPLICATION_IN_TELEMARKETING
      ),
      id: uuidv4(),
      title: 'flow-inner-check'
    }
  }, [
    COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED,
    CREATE_APPLICATION_IN_TELEMARKETING,
    FETCH_PERSONAL_INFO,
    currentFlowType
  ])

  const secondStepsStatus = useMemo<StepType>(() => {
    if (!currentFlowType) {
      return {
        status: 'STEP_NOT_STARTED',
        id: uuidv4(),
        title: 'flow-outside-check'
      }
    }
    return {
      status: getWsStepStatuses(PRE_SCORE, OPERATOR_BMG, GENERATE_DOCUMENTS_SIGNED_BY_OTP),
      id: uuidv4(),
      title: 'flow-outside-check'
    }
  }, [GENERATE_DOCUMENTS_SIGNED_BY_OTP, OPERATOR_BMG, PRE_SCORE, currentFlowType])

  const stepsArray = useMemo(() => [firstStepsStatus, secondStepsStatus], [firstStepsStatus, secondStepsStatus])

  useEffect(() => {
    const checkMainFlowSteps = async () => {
      if (checkStepAndProcess(getWsStepStatus(FETCH_PERSONAL_INFO), progress < 15)) {
        setProgress(getRandomNumberBetweenRange(5, 15))
        await fetchUser()
      }
      if (checkStepAndProcess(getWsStepStatus(GENERATE_DOCUMENTS_SIGNED_BY_OTP), progress < 40)) {
        setProgress(getRandomNumberBetweenRange(15, 40))
      }
      if (checkStepAndProcess(getWsStepStatus(COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED), progress < 50)) {
        setProgress(getRandomNumberBetweenRange(40, 50))
      }
      if (checkStepAndProcess(getWsStepStatuses(CREATE_APPLICATION_IN_TELEMARKETING, OPERATOR_BMG), progress < 70)) {
        setProgress(getRandomNumberBetweenRange(50, 70))
      }
      if (getWsStepStatus(PRE_SCORE) === 'DONE') {
        setProgress(100)
      } else if (
        getWsStepStatus(PRE_SCORE) === 'REJECTED' ||
        getWsStepStatus(OPERATOR_BMG) === 'REJECTED' ||
        credit_status === 'FAILED' ||
        data?.credit_status === 'FAILED'
      ) {
        dispatch(setIsRejected(true))
        return navigate(pathnames.rejected)
      }
    }

    checkMainFlowSteps()
  }, [
    COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED,
    CREATE_APPLICATION_IN_TELEMARKETING,
    FETCH_PERSONAL_INFO,
    GENERATE_DOCUMENTS_SIGNED_BY_OTP,
    OPERATOR_BMG,
    PRE_SCORE,
    credit_status,
    data?.credit_status,
    dispatch,
    fetchUser,
    navigate,
    progress
  ])

  useEffect(() => {
    if (!data) return
    const current_step = data.current_step
    if (!current_step) return
    const { status, step } = current_step
    if (PRESCORING_PAGE_STEPS.includes(step as FLOW_STEPS) && status === 'DONE') {
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    }
  }, [data])

  useEffect(() => {
    let timer: number | undefined
    const checkProgressAndGoNextPage = async () => {
      if (progress === 100) {
        timer = setTimeout(async () => {
          Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
        }, 300)
      }
    }

    checkProgressAndGoNextPage()
    return () => {
      clearTimeout(timer)
    }
  }, [progress])

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress === 100) {
          clearInterval(timer)
        }
        if (oldProgress > PROGRESS_LIMIT) {
          return oldProgress
        }
        return oldProgress + 1
      })
    }, 1500)

    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <div tw='px-4 lg:px-0 min-h-[calc(100vh - 86px)] flex flex-col items-center justify-center'>
      <Typography variant='subbody' twStyle={tw`text-grey-dark`}>
        {t('flow-please-wait')}
      </Typography>
      <Heading variant='title2' weight='bold' twStyle={tw`mt-0.5`}>
        {t('flow-process-ongoing')}
      </Heading>
      <LinearProgress
        percent={progress}
        strokeColor='var(--primary-color-middle)'
        trailColor='var(--green-light)'
        tw='mt-24 mb-6'
      />
      <Heading variant='title1' tw='text-primary'>
        {progress}%
      </Heading>
      <ul tw='space-y-6 mt-10'>
        {stepsArray.map(step => (
          <li tw='flex items-center space-x-4' key={step.id}>
            {step.status === 'DONE' ? (
              <span tw='w-6 h-6 inline-flex items-center justify-center flex-shrink-0'>
                <CheckIcon tw='w-6 h-6' />
              </span>
            ) : ['FAILED', 'RETRY'].includes(step.status as string) ? (
              <span tw='w-6 h-6 inline-flex items-center justify-center flex-shrink-0'>
                <CloseIcon tw='text-primary-middle w-4 h-4' />
              </span>
            ) : (
              <span tw='w-6 h-6 inline-flex items-center justify-center flex-shrink-0'>
                <Ring color='var(--primary-color)' size={24} />
              </span>
            )}
            <Typography variant='subbody'>{t(step.title)}</Typography>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PrescoringLoader
